import React, { useRef } from "react";
import styled from "styled-components";
import { CSSTransition } from 'react-transition-group';
import Modal from 'react-modal';
import { useTranslation } from "react-i18next";

import useLoadingStore from "../../store/useLoadingStore";
import useModalStore from "../../store/useModalStore";
import useApplyStore from "../../store/useApplyStore";
import useViewStore from "../../store/useViewStore";
import FormButtonSecondary from "../form/FormButtonSecondary";
import FormButtonPrimary from "../form/FormButtonPrimary";
import FormButtonDanger from "../form/FormButtonDanger";
import LoadingIcon from "../core/LoadingIcon";
import Space from "../core/Space";
import ShadowBox from "../core/ShadowBox";
import Flex from "../core/Flex";
import FormRemark from "../form/FormRemark";
import BlueBox from "../common/BlueBox";
import FormText from "../form/FormText";
import TextArea from "antd/es/input/TextArea";
import { isProduction } from "../../setting";

const ModalStartApply = ({ children }) => {
  const { t } = useTranslation();
  const { isModalStartApplyOpen, closeModalStartApply } = useModalStore();
  const { isMobileView } = useViewStore((state) => state);
  const { isQ1Option1Active, isQ2Option1Active, hospital } = useApplyStore();
  const { goNextStep } = useApplyStore();

  return (
    <Modal
      // For open timeout, please find in index.css
      closeTimeoutMS={250}
      isOpen={isModalStartApplyOpen}
      onRequestClose={closeModalStartApply}
      style={customStyles}
      ariaHideApp={false}
    >
      <ModalContent $isMobileView={isMobileView}>
        <ModalTitle $isMobileView={isMobileView}>{t("translation:apply.step0Modal.title", { key: isQ1Option1Active && isQ2Option1Active && hospital?.value !== "00000000000000000000000000000000" ? t("term.preAuth") : t("term.PCA") })}</ModalTitle>
        <ModalText $isMobileView={isMobileView}>{t("translation:apply.step0Modal.prepare")}</ModalText>

        <Flex direction={isMobileView ? "column" : "row"} gap={16} style={isMobileView ? { height: 200 } : { alignItems: "stretch" }}>
          <ShadowBox style={{ flex: 1 }}>
            <img src={`${process.env.PUBLIC_URL}/images/credit-card.png`} alt="selected" />
            <Flex direction={"column"} alignItems={"flex-start"} gap={0}>
              <ModalText style={{ color: "var(--aia-bx-dark-blue)", fontWeight: "bold" }}>{t("translation:apply.step0Modal.box1.title")}</ModalText>
              <FormRemark>{t("translation:apply.step0Modal.box1.content")}</FormRemark>
            </Flex>
          </ShadowBox>
          <ShadowBox style={{ flex: 1 }}>
            <img src={`${process.env.PUBLIC_URL}/images/documents-3pcs.png`} alt="selected" />
            <Flex direction={"column"} alignItems={"flex-start"} gap={0}>
              <ModalText style={{ color: "var(--aia-bx-dark-blue)", fontWeight: "bold" }}>{t("translation:apply.step0Modal.box2.title")}</ModalText>
              <FormRemark>{t("translation:apply.step0Modal.box2.content")}</FormRemark>
            </Flex>
          </ShadowBox>
        </Flex>

        <Space height={24} />

        <BlueBox>
          <FormText style={{ textAlign: "center" }}>{t("translation:apply.step0Modal.box3.content")}</FormText>
          <DownloadButton onClick={() => {
            const url = `${process.env.REACT_APP_PDF_PREFIX}/MC223_Pre-assessment Service (2024_1031).pdf`;
            window.open(url, '_blank');
          }}><DownloadButtonText>{t("translation:apply.step0Modal.box3.btn")}</DownloadButtonText></DownloadButton>
        </BlueBox>

        <Space height={32} />

        <ModalButtonRow $isMobileView={isMobileView}>
          <FormButtonSecondary onClick={closeModalStartApply}>{t("cancel")}</FormButtonSecondary>
          <FormButtonDanger id={isProduction ? "pre_auth_apply_apply_btn" : "pre_auth_uat_apply_apply_btn"} onClick={goNextStep}>{t("translation:apply.step0Modal.btn")}</FormButtonDanger>
        </ModalButtonRow>
      </ModalContent>
    </Modal >
  );
};

const customStyles = {
  overlay: {
    background: 'rgba(0, 0, 0, 0.3)',
    zIndex: 900,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    padding: 0,
    border: 0,
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: 15,
    maxWidth: 680,
    width: "95%",
    maxHeight: "95%",
  },
};

const ModalTitle = styled.div`
  font-size: 20px;
  line-height: 30px;
  font-weight: bold;
  color: var(--aia-bx-dark-blue);
  margin-bottom: 40px;

  ${props => props.$isMobileView && `
    text-align: center;
  `}
`;

const ModalText = styled.div`
  font-size: 16px;
  line-height: 26px;
  color: var(--aia-bx-black);
  margin-bottom: 8px;

  ${props => props.$isMobileView && `
    text-align: center;
  `}
`;

const ModalButtonRow = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;

  ${props => props.$isMobileView && `
    flex-direction: column-reverse;
    gap: 10px;
  `}
`;

const ModalContent = styled.div`
  ${props => props.$isMobileView ? `padding: 40px 16px;` : `padding: 40px 32px;`}
`;

const DarkBlueBox = styled.div`
  display: flex;
  background-color: var(--aia-bx-dark-blue);
  padding: 20px;
  gap: 20px;
  align-items: center;

  ${props => props.$isMobileView && `
    flex-direction: column;
  `}
`;

const DarkBlueBoxText = styled.div`
  color: var(--aia-bx-white);
  font-size: 14px;
  flex: 1;
`;

const DarkBlueBoxButton = styled.button`
  border: 1px solid var(--aia-bx-blue-70);
  background-color: transparent;
  border-radius: 50px;
  cursor: pointer;
  padding: 10px 20px;
  width: auto;
  
  &:hover {
    background-color: var(--aia-bx-blue-70);
  }
`;

const DarkBlueBoxButtonText = styled.div`
  font-size: 12px;
  color: var(--aia-bx-white);
`;

const DownloadButtonText = styled.div`
  font-size: 12px;
  color: var(--aia-bx-dark-blue);
`;

const DownloadButton = styled.button`
  border: 1px solid var(--aia-bx-blue-2);
  background-color: var(--aia-bx-white);
  border-radius: 50px;
  cursor: pointer;
  padding: 10px 20px;
  width: auto;
  
  &:hover {
  background-color: var(--aia-bx-blue-2);

    ${DownloadButtonText} {
    color: var(--aia-bx-white);
  }
}
`;


export default ModalStartApply;